import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { XIcon } from '@heroicons/react/outline';
import {
  ReturnBlockedCustomer,
  ReturnCustomerRestrictions,
  getReturnCustomerRestrictionErrorCode,
} from 'types/returnCustomerRestrictions';

type Props = {
  value: ReturnCustomerRestrictions;
  onChange: (value: ReturnCustomerRestrictions) => void;
  onLookupOrder: (orderNumber: string) => Promise<ReturnBlockedCustomer>;
  disabled: boolean;
};

const inputClass =
  'min-w-0 flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50';
const buttonClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50';
const lookupErrors: Record<string, string> = {
  RETURN_CUSTOMER_ORDER_NOT_FOUND:
    'No order was found in this store. Check the order number and try again.',
  RETURN_CUSTOMER_ORDER_AMBIGUOUS:
    'More than one order matched. Enter the complete order number, including any prefix or suffix.',
  RETURN_CUSTOMER_ORDER_UNAVAILABLE:
    'Order lookup is temporarily unavailable. Try again.',
  RETURN_CUSTOMER_ORDER_IDENTITY_UNAVAILABLE:
    'This order has no customer or usable email address to block.',
};
const customerLabel = (customer: ReturnBlockedCustomer) =>
  customer.customerName ||
  customer.email ||
  `Customer from ${customer.orderNumber}`;
const sameCustomer = (
  left: ReturnBlockedCustomer,
  right: ReturnBlockedCustomer,
) =>
  left.orderId === right.orderId ||
  (left.customerId && right.customerId
    ? left.customerId === right.customerId
    : !left.customerId &&
      !right.customerId &&
      left.email?.toLowerCase() === right.email?.toLowerCase());

export const CustomerRestrictionIdentities = ({
  value,
  onChange,
  onLookupOrder,
  disabled,
}: Props) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [orderError, setOrderError] = useState('');
  const [customer, setCustomer] = useState<ReturnBlockedCustomer | null>(null);
  const [finding, setFinding] = useState(false);
  const request = useRef(0);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  useLayoutEffect(() => {
    if (disabled) {
      request.current += 1;
      setFinding(false);
      setCustomer(null);
      setOrderError('');
    }
  }, [disabled]);

  const addEmail = () => {
    if (disabled) return;
    const normalized = email.trim().toLowerCase();
    if (
      normalized.length > 254 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)
    ) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (value.blockedEmails.includes(normalized)) {
      setEmailError('This email is already added.');
      return;
    }
    if (value.blockedEmails.length >= 100) {
      setEmailError('You can add up to 100 email addresses.');
      return;
    }
    onChange({ ...value, blockedEmails: [...value.blockedEmails, normalized] });
    setEmail('');
    setEmailError('');
  };

  const findCustomer = async () => {
    if (disabled || finding || !orderNumber.trim()) return;
    const current = ++request.current;
    setFinding(true);
    setOrderError('');
    setCustomer(null);
    try {
      const result = await onLookupOrder(orderNumber.trim());
      if (mounted.current && current === request.current) setCustomer(result);
    } catch (error) {
      if (mounted.current && current === request.current) {
        setOrderError(
          lookupErrors[getReturnCustomerRestrictionErrorCode(error)] ||
            'Could not find a customer for this order. Check the order number and try again.',
        );
      }
    } finally {
      if (mounted.current && current === request.current) setFinding(false);
    }
  };

  const addCustomer = () => {
    if (disabled || !customer) return;
    if (
      value.blockedCustomers.some((existing) =>
        sameCustomer(existing, customer),
      )
    ) {
      setOrderError('This customer is already added.');
      return;
    }
    if (value.blockedCustomers.length >= 100) {
      setOrderError('You can add up to 100 customers from orders.');
      return;
    }
    onChange({
      ...value,
      blockedCustomers: [...value.blockedCustomers, customer],
    });
    setCustomer(null);
    setOrderNumber('');
    setOrderError('');
  };

  return (
    <fieldset
      disabled={disabled}
      className={`space-y-5 border-t border-gray-100 pt-5 ${
        !value.enabled ? 'opacity-60' : ''
      }`}
    >
      <div>
        <label
          htmlFor="blocked-customer-email"
          className="mb-2 block text-sm font-medium text-gray-900"
        >
          Customer email
        </label>
        <div className="flex gap-2">
          <input
            id="blocked-customer-email"
            type="email"
            value={email}
            placeholder="customer@example.com"
            aria-describedby="customer-email-help"
            className={inputClass}
            onChange={(event) => {
              setEmail(event.target.value);
              setEmailError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addEmail();
              }
            }}
          />
          <button
            type="button"
            onClick={addEmail}
            disabled={disabled || !email.trim()}
            className={buttonClass}
          >
            Add email
          </button>
        </div>
        <p
          id="customer-email-help"
          className="mt-2 text-xs leading-5 text-gray-500"
        >
          Blocks new returns and exchanges for orders using this exact email address, ignoring
          capitalization.
        </p>
        {emailError && (
          <p role="alert" className="mt-2 text-sm text-red-700">
            {emailError}
          </p>
        )}
        {value.blockedEmails.length > 0 && (
          <ul
            aria-label="Blocked customer emails"
            className="mt-3 flex flex-wrap gap-2"
          >
            {value.blockedEmails.map((blockedEmail) => (
              <li
                key={blockedEmail}
                className="inline-flex max-w-full items-center gap-2 rounded-md border border-gray-200 bg-gray-100 py-1.5 pl-2.5 pr-1.5 text-sm text-gray-800"
              >
                <span className="break-all">{blockedEmail}</span>
                <button
                  type="button"
                  aria-label={`Remove email ${blockedEmail}`}
                  className="rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900"
                  onClick={() =>
                    onChange({
                      ...value,
                      blockedEmails: value.blockedEmails.filter(
                        (item) => item !== blockedEmail,
                      ),
                    })
                  }
                >
                  <XIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <label
          htmlFor="blocked-customer-order"
          className="mb-2 block text-sm font-medium text-gray-900"
        >
          Order number
        </label>
        <div className="flex gap-2">
          <input
            id="blocked-customer-order"
            type="text"
            value={orderNumber}
            placeholder="e.g. #1001"
            aria-describedby="customer-order-help"
            className={inputClass}
            onChange={(event) => {
              request.current += 1;
              setFinding(false);
              setOrderNumber(event.target.value);
              setOrderError('');
              setCustomer(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void findCustomer();
              }
            }}
          />
          <button
            type="button"
            onClick={findCustomer}
            disabled={disabled || finding || !orderNumber.trim()}
            className={buttonClass}
          >
            {finding ? 'Finding customer…' : 'Find customer'}
          </button>
        </div>
        <p
          id="customer-order-help"
          className="mt-2 text-xs leading-5 text-gray-500"
        >
          Find an order from this store to select its customer. Review who will
          be blocked before adding them.
        </p>
        {orderError && (
          <p role="alert" className="mt-2 text-sm text-red-700">
            {orderError}
          </p>
        )}
        {customer && (
          <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <p className="text-sm font-semibold text-gray-900">
              {customerLabel(customer)}
            </p>
            <p className="mt-1 text-sm text-gray-600">
              {customer.orderNumber}
              {customer.email ? ` · ${customer.email}` : ''}
            </p>
            <p className="mt-2 text-sm text-gray-700">
              {customer.customerId
                ? 'Blocks new returns and exchanges for this Shopify customer across all their orders, including future purchases.'
                : `Guest checkout: blocks new returns and exchanges for orders using ${customer.email}.`}
            </p>
            <button
              type="button"
              onClick={addCustomer}
              disabled={disabled}
              className={`${buttonClass} mt-3`}
            >
              Add customer
            </button>
          </div>
        )}
        {value.blockedCustomers.length > 0 && (
          <ul
            aria-label="Blocked customers from orders"
            className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200"
          >
            {value.blockedCustomers.map((blockedCustomer) => (
              <li
                key={blockedCustomer.orderId}
                className="flex items-start justify-between gap-3 px-3 py-2"
              >
                <div className="min-w-0 text-sm">
                  <p className="break-words font-medium text-gray-900">
                    {customerLabel(blockedCustomer)}
                  </p>
                  <p className="mt-0.5 break-all text-xs text-gray-500">
                    From order {blockedCustomer.orderNumber}
                    {blockedCustomer.email ? ` · ${blockedCustomer.email}` : ''}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {blockedCustomer.customerId
                      ? 'All orders for this Shopify customer'
                      : 'Orders with this email address'}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={`Remove customer ${customerLabel(
                    blockedCustomer,
                  )}`}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  onClick={() =>
                    onChange({
                      ...value,
                      blockedCustomers: value.blockedCustomers.filter(
                        (item) => item.orderId !== blockedCustomer.orderId,
                      ),
                    })
                  }
                >
                  <XIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-gray-500">
        Add or remove entries, then save the policy to apply your changes.
      </p>
    </fieldset>
  );
};
